import React, { useEffect, useState } from 'react';
import Input from '../../Inputs/Input';
import { useDispatch } from 'react-redux';
import {
  ReturnFormActions,
  ReturnItemKeys,
  ReturnItemProps,
} from '../../../Reducers/ReturnForm.reducer';
import Icon from '../../Icon';
import ReturnFormMediaInput from './ReturnFormMediaInput';
import Dropdown from '../../Dropdown';
import { translate } from '../../../Services/translation';
import ReturnFormMediaItem from './ReturnFormMediaItem';
import { ReturnFormItemValidationSchema } from '../misc/ReturnFormValidationSchema';

const ReturnFormItem = ({
  item,
  errorCodes,
  index,
}: {
  item: ReturnItemProps;
  errorCodes: string[];
  index: number;
}) => {
  const dispatch = useDispatch();

  const { validationErrors, uniqueId } = item;

  const validateOnBlur = async (key: string, value: string) => {
    try {
      await ReturnFormItemValidationSchema.validateAt(
        key,
        { ...ReturnFormItem, [key]: value },
        { abortEarly: true }
      );

      dispatch(
        ReturnFormActions.setRowValidationErrors({
          key: key,
          id: uniqueId,
          error: undefined,
        })
      );
    } catch (err) {
      dispatch(
        ReturnFormActions.setRowValidationErrors({
          id: uniqueId,
          key: key,
          error: err.message,
        })
      );
    }
  };

  const getValidationErrorLabel = (key: string) => {
    const errMsg = validationErrors[key];
    return !!errMsg ? errMsg : undefined;
  };

  return (
    <div className="mb-4 grid grid-cols-4 gap-4 rounded border border-gray-200 bg-gray-50 p-4 md-down:grid-cols-2">
      <div className="col-span-4 flex justify-between md-down:col-span-2">
        <p className="text-lg font-medium leading-6 text-gray-700">
          {translate('return.form-product')} {index}
        </p>
        <button
          tabIndex={-1}
          onClick={() => dispatch(ReturnFormActions.removeReturnItem({id: item.uniqueId}))}
        >
          <Icon
            name="delete"
            className="h-6 w-6"
            onClick={() => {
              dispatch(
                ReturnFormActions.removeReturnItem({ id: item.uniqueId })
              );
            }}
          />
        </button>
      </div>
      <Input
        inputSize="medium"
        showAnimatedLabel={true}
        label={translate('return.form-item.ordernr')}
        onChange={(e) => {
          dispatch(
            ReturnFormActions.updateReturnItem({
              key: ReturnItemKeys.orderNumber,
              value: e.target.value,
              id: item.uniqueId,
            })
          );
        }}
        onBlur={(e) =>
          validateOnBlur(ReturnItemKeys.orderNumber, e.target.value)
        }
        message={getValidationErrorLabel(ReturnItemKeys.orderNumber)}
        inputState={
          !!validationErrors[ReturnItemKeys.orderNumber]
            ? 'error'
            : 'default'
        }
        required
      />
      <Input
        inputSize="medium"
        showAnimatedLabel={true}
        label={translate('return.form-item.articlenr')}
        onChange={(e) => {
          dispatch(
            ReturnFormActions.updateReturnItem({
              key: ReturnItemKeys.articleNumber,
              value: e.target.value,
              id: item.uniqueId,
            })
          );
        }}
        onBlur={(e) =>
          validateOnBlur(ReturnItemKeys.articleNumber, e.target.value)
        }
        message={getValidationErrorLabel(ReturnItemKeys.articleNumber)}
        inputState={
          !!validationErrors[ReturnItemKeys.articleNumber]
            ? 'error'
            : 'default'
        }
        required
      />
      <Input
        inputSize="medium"
        showAnimatedLabel={true}
        label={translate('return.form-item.batchnr')}
        onChange={(e) => {
          dispatch(
            ReturnFormActions.updateReturnItem({
              key: ReturnItemKeys.batchNumber,
              value: e.target.value,
              id: item.uniqueId,
            })
          );
        }}
        onBlur={(e) =>
          validateOnBlur(ReturnItemKeys.batchNumber, e.target.value)
        }
        message={getValidationErrorLabel(ReturnItemKeys.batchNumber)}
        inputState={
          !!validationErrors[ReturnItemKeys.batchNumber]
            ? 'error'
            : 'default'
        }
        required
      />
      <Input
        inputSize="medium"
        showAnimatedLabel={true}
        type="number"
        label={translate('return.form-item.quantity')}
        onChange={(e) => {
          dispatch(
            ReturnFormActions.updateReturnItem({
              key: ReturnItemKeys.quantity,
              value: e.target.value,
              id: item.uniqueId,
            })
          );
        }}
        onBlur={(e) =>
          validateOnBlur(ReturnItemKeys.quantity, e.target.value)
        }
        message={getValidationErrorLabel(ReturnItemKeys.quantity)}
        inputState={
          !!validationErrors[ReturnItemKeys.quantity] ? 'error' : 'default'
        }
        required
      />
      <Dropdown
        size="large"
        label={translate('return.form-item.error-code')}
        selectClassName="max-h-[42px] text-ellipsis text-md"
        placeholder={translate('return.form-error.code-placeholder')}
        onChange={({ value }) => {
          dispatch(
            ReturnFormActions.updateReturnItem({
              key: ReturnItemKeys.errorCode,
              value: value,
              id: item.uniqueId,
            })
          );
        }}
        onBlur={(e) => {
          validateOnBlur(ReturnItemKeys.errorCode, e.value);
        }}
        selectedValue={item.errorCode}
        options={
          errorCodes?.length > 0
            ? errorCodes.map((code) => {
                return {
                  label: code,
                  value: code,
                  disabled: code ? false : true,
                  object: code,
                  selectedlabel: code.substring(0, 20) + '...',
                };
              })
            : []
        }
        disabled={errorCodes?.length < 0}
        className="col-span-2 md:max-h-[42px]"
        activeEffect={true}
      />
      <Input
        inputSize="medium"
        wrapperClassName="col-span-2"
        showAnimatedLabel={true}
        label={translate('return.form-item.comments')}
        onChange={(e) => {
          dispatch(
            ReturnFormActions.updateReturnItem({
              key: ReturnItemKeys.comment,
              value: e.target.value,
              id: item.uniqueId,
            })
          );
        }}
      />
      <ReturnFormMediaInput item={item} />

      {item.images?.length > 0 && (
        <div className="col-span-full flex flex-col gap-2">
          {item.images.map((image, idx) => {
            return (
              <ReturnFormMediaItem
                id={item.uniqueId}
                item={image}
                key={image.name + idx}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReturnFormItem;
